import React from 'react';
import { useSelector } from 'react-redux';
import { Pressable, Image } from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import {
  BodyText,
  HeaderText,
  Stack,
  Absolute,
} from '../../../ui';
import { useIsMobile } from '../../../../hooks';
import { BasicStarFilledIcon } from '../../../ui/Icons';

import BookmarkIcon from './BookmarkIcon/BookmarkIcon';
import DirectShareButton from '../../../consumer/sharing/DirectShareButton';
import { PublicProvider } from '../../../../api/Providers';
import type { RootState } from '../../../../store/models';
import type { State as CurrentUserState, WhoAmI } from '../../../../store/CurrentUser.types';
import { ShareTarget } from '../../../../modules/share';
import { getProfileKeyHighlights } from '../../../../modules/provider/profileKeyHighlights';

import { ShareProps } from '../../../consumer/sharing';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { getBusinessName } from '../../../../api/ProfileClientView/ProfileClientView';
import { TopProBadge } from '../../TopPro/TopProBadge';

type ProfileActionsProps = {
  provider: PublicProvider | ClientProviderProfile;
  isBookmarked: boolean;
  user?: CurrentUserState;
  shareConfig?: Omit<ShareProps, 'user'>;
  onBookmarkClick: () => void;
};

const ProfileActions: React.FCWithChildren<ProfileActionsProps> = ({
  isBookmarked,
  provider,
  user,
  shareConfig,
  onBookmarkClick,
}) => (
  !!shareConfig
  && (
    <Inline space={8}>
      <Box>
        {/* Hearting the pro */}
        <BookmarkIcon
          onClick={onBookmarkClick}
          bookmarked={isBookmarked}
        />
      </Box>
      <Box flexGrow={0} flexBasis="auto">
        {/* Share profile */}
        <DirectShareButton
          {...shareConfig}
          appearance="basic-share-arrow-icon"
          target={ShareTarget.PROFILE}
          provider={provider}
          user={user as WhoAmI}
        />
      </Box>
    </Inline>
  )
);

interface NameBoxProps {
  name: string;
  pronouns?: string;
}

const NameBox: React.FC<NameBoxProps> = ({ name, pronouns }) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Stack width={166} alignItems="flex-start">
        <HeaderText
          ariaLevel={2}
          level={2}
          testID="proName"
          color={BrandColor.BackInBlack}
        >
          {name}
        </HeaderText>
        {pronouns && (
          <BodyText
            appearance="medium"
            color={BrandColor.Ash}
          >
            ({pronouns})
          </BodyText>
        )}
      </Stack>
    );
  }

  return (
    <Inline alignItems="center" maxWidth="100%">
      <HeaderText
        ariaLevel={2}
        level={2}
        testID="proName"
        color={BrandColor.BackInBlack}
      >
        {name}
      </HeaderText>
      {pronouns && (
        <BodyText
          appearance="medium"
          color={BrandColor.Ash}
        >
          ({pronouns})
        </BodyText>
      )}
    </Inline>
  );
};

export type ProBioProps = {
  provider: PublicProvider | ClientProviderProfile;
  isEditMode: boolean;
  isTopPro: boolean;
  ratingCount?: number;
  reviewCount?: number;
  isBookmarked: boolean;
  isOwnProfile: boolean;
  onProfilePhotoClick: () => void;
  onBookmarkClick: () => void;
  onTopProClick?: () => void;
  shareConfig: Omit<ShareProps, 'user'>;
};

export const ProBio: React.FCWithChildren<ProBioProps> = ({
  provider,
  isEditMode,
  isTopPro,
  ratingCount,
  isBookmarked,
  onProfilePhotoClick,
  onBookmarkClick,
  onTopProClick,
  shareConfig,
}) => {
  const user = useSelector<RootState, CurrentUserState>(
    state => state.user,
    (prevUser, newUser) => prevUser.userId === newUser.userId,
  );
  const {
    profile_photo: profilePhoto,
    name,
    profession,
    rating_stars: ratingStars,
  } = provider;
  const highlights = getProfileKeyHighlights(provider);
  const has1KPusBooking = highlights.oneThousandPlusBooked;
  const isMobile = useIsMobile();
  const businessName = isEditMode && !getBusinessName(provider) ? name : getBusinessName(provider);
  const imageSize = isMobile ? 72 : 96;

  return (
    <Inline
      padding={isMobile ? 24 : 0}
    >
      {/* Profile Image */}
      <Box paddingRight={16} testID="pro-profile-image">
        <Pressable onPress={onProfilePhotoClick} testID="profilePhoto">
          <Box
            dataSet={{ photoUrl: profilePhoto }}
            width={imageSize}
            height={imageSize}
          >
            <Image
              source={profilePhoto ? `${profilePhoto}_200x200.jpg` : null}
              style={{
                height: imageSize,
                width: imageSize,
                borderRadius: 100,
                backgroundColor: BrandColor.LightGrey,
              }}
            />
          </Box>
        </Pressable>
      </Box>
      {/* Pro name  and action items */}
      <Stack
        width={isMobile ? 240 : null}
        flexGrow={1}
      >
        {/* Pro name and action items */}
        <Inline
          paddingBottom={8}
          width="100%"
        >
          <NameBox name={name} pronouns={provider.preferred_pronouns} />
          <Absolute right={isMobile ? 0 : 0} testID="pro-bio-action-items">
            <ProfileActions
              shareConfig={shareConfig}
              isBookmarked={isBookmarked}
              provider={provider}
              user={user}
              onBookmarkClick={onBookmarkClick}
            />
          </Absolute>
        </Inline>
        {/* Profession &&  Business name */}
        <Box>
          <BodyText
            appearance="normal"
            testID="proProfession"
            color={BrandColor.Metal}
          >
            {profession} {businessName && `at ${businessName}`}
          </BodyText>
        </Box>
        <Inline space={10} paddingTop={12} alignItems="baseline">
          {/* Top Pro */}
          {isTopPro && (
            <Pressable onPress={onTopProClick} testID="TopProBadge">
              <TopProBadge />
            </Pressable>
          )}
          <Inline space={4}>
            {/* Rating */}
            {ratingCount && (
              <Inline
                testID="rating-stars"
                space={2}
                alignItems="center"
              >
                <Box>
                  <BasicStarFilledIcon
                    width={16}
                    height={16}
                    color={BrandColor.BackInBlack}
                  />
                </Box>
                <BodyText appearance="normal" testID="rating-stars-average-rate">
                  {ratingStars.toFixed(1)}
                </BodyText>
                <BodyText testID="ss-pro-ratings-count" appearance="normal">
                  ({ratingCount})
                </BodyText>
              </Inline>
            )}
            {/* 1K+ booked */}
            {has1KPusBooking && (
              <React.Fragment>
                <Box
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box
                    width={2}
                    height={2}
                    borderRadius={100}
                    color={BrandColor.LightGrey}
                    testID="proBioDot"
                    justifyContent="center"
                    alignItems="center"
                  />
                </Box>
                <Box
                  testID="pro-1k+-booked"
                  justifyContent="center"
                  alignItems="center"
                >
                  <BodyText color={BrandColor.Metal}>1K+ booked</BodyText>
                </Box>
              </React.Fragment>
            )}
          </Inline>
        </Inline>
      </Stack>
    </Inline>
  );
};
